import { useMemo, useState } from "react";
import { Check, Copy } from "@fg/icons";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import type { Message } from "@/api/mailApi";
import { cn } from "@/lib/utils";
import { parseAuthResults, verdictLabel, type AuthVerdict } from "@/lib/authResults";

const tone = (v?: AuthVerdict) =>
    v === "pass" ? "text-emerald-600" : v === "fail" || v === "softfail" || v === "permerror" ? "text-red-600" : "text-muted-foreground";

/**
 * "View message details": who really sent it (SPF / DKIM / DMARC as our server checked them) and the full header block,
 * searchable and copyable, as in Outlook's "View message source" and Gmail's "Show original".
 */
export function MessageSourceDialog({ message, open, onOpenChange }: { message: Message; open: boolean; onOpenChange: (open: boolean) => void }) {
    const [filter, setFilter] = useState("");
    const [copied, setCopied] = useState(false);
    const auth = useMemo(() => parseAuthResults(message.authenticationResults), [message.authenticationResults]);
    const lines = useMemo(() => (message.rawHeaders ?? "").split("\n").filter(Boolean), [message.rawHeaders]);
    const shown = filter.trim() ? lines.filter((l) => l.toLowerCase().includes(filter.trim().toLowerCase())) : lines;

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(message.rawHeaders ?? "");
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1500);
        } catch { /* clipboard blocked: the text is still selectable */ }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="flex max-h-[85vh] max-w-3xl flex-col gap-3">
                <DialogHeader>
                    <DialogTitle>Message details</DialogTitle>
                    <DialogDescription className="truncate">{message.subject || "(no subject)"} · from {message.from.email}</DialogDescription>
                </DialogHeader>

                <section aria-label="Sender verification" className="rounded-md border p-3 text-sm">
                    <p className="mb-1.5 font-medium">
                        Sender verification:{" "}
                        <span className={cn(auth.overall === "verified" ? "text-emerald-600" : auth.overall === "failed" ? "text-red-600" : "text-muted-foreground")}>
                            {auth.overall === "verified" ? "verified" : auth.overall === "failed" ? "failed" : "no result from our server"}
                        </span>
                    </p>
                    <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-0.5">
                        {(["spf", "dkim", "dmarc"] as const).map((k) => (
                            <div key={k} className="contents">
                                <dt className="uppercase text-muted-foreground">{k}</dt>
                                <dd className={tone(auth[k])}>{verdictLabel(auth[k])}</dd>
                            </div>
                        ))}
                    </dl>
                    <p className="mt-2 text-xs text-muted-foreground">
                        SPF checks the sending server, DKIM checks the message wasn't altered, DMARC ties both to the From address. A failure doesn't prove fraud, but treat links and attachments with care.
                    </p>
                </section>

                {lines.length === 0 ? (
                    <p className="text-sm text-muted-foreground">The headers aren't available for this message.</p>
                ) : (
                    <>
                        <div className="flex items-center gap-2">
                            <input
                                value={filter}
                                onChange={(e) => setFilter(e.target.value)}
                                placeholder="Filter headers (e.g. received, dkim)"
                                aria-label="Filter headers"
                                className="h-8 flex-1 rounded-md border bg-background px-2 text-sm outline-none focus:ring-2 focus:ring-ring"
                            />
                            <Button type="button" variant="outline" size="sm" onClick={() => void copy()}>
                                {copied ? <Check className="mr-1 h-4 w-4" /> : <Copy className="mr-1 h-4 w-4" />}
                                {copied ? "Copied" : "Copy all"}
                            </Button>
                        </div>
                        <pre className="min-h-0 flex-1 overflow-auto whitespace-pre-wrap break-all rounded-md border bg-muted/40 p-3 font-mono text-xs leading-relaxed" tabIndex={0} aria-label="Message headers">
                            {shown.join("\n") || "No header matches."}
                        </pre>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}
